import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { resolveImg } from "@/lib/api";

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const STEP = 0.5;

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/**
 * Full-screen image viewer for the Product Detail gallery.
 * Supports scroll-to-zoom, double-click/tap-to-zoom, drag-to-pan when zoomed,
 * keyboard navigation (Esc / Arrow keys / +/-) and prev/next across images.
 * Deliberately NOT built on the shared Dialog primitive — a true full-viewport
 * stage with custom zoom/pan math is simpler to control as a standalone overlay.
 */
export function ImageZoomViewer({ images, index, onIndexChange, open, onOpenChange, alt }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    if (open) {
      setScale(1);
      setPos({ x: 0, y: 0 });
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open, index]);

  const zoomIn = useCallback(() => setScale((s) => clamp(+(s + STEP).toFixed(2), MIN_SCALE, MAX_SCALE)), []);
  const zoomOut = useCallback(() => setScale((s) => {
    const next = clamp(+(s - STEP).toFixed(2), MIN_SCALE, MAX_SCALE);
    if (next === 1) setPos({ x: 0, y: 0 });
    return next;
  }), []);
  const resetZoom = useCallback(() => { setScale(1); setPos({ x: 0, y: 0 }); }, []);

  const goPrev = useCallback(() => {
    resetZoom();
    onIndexChange((index - 1 + images.length) % images.length);
  }, [index, images.length, onIndexChange, resetZoom]);
  const goNext = useCallback(() => {
    resetZoom();
    onIndexChange((index + 1) % images.length);
  }, [index, images.length, onIndexChange, resetZoom]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onOpenChange(false);
      else if (e.key === "ArrowLeft" && images.length > 1) goPrev();
      else if (e.key === "ArrowRight" && images.length > 1) goNext();
      else if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-") zoomOut();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, images.length, goPrev, goNext, zoomIn, zoomOut, onOpenChange]);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? STEP : -STEP;
    setScale((s) => {
      const next = clamp(+(s + delta).toFixed(2), MIN_SCALE, MAX_SCALE);
      if (next === 1) setPos({ x: 0, y: 0 });
      return next;
    });
  };

  const onDoubleClick = () => { scale > 1 ? resetZoom() : setScale(2.5); };

  const onPointerDown = (e) => {
    if (scale <= 1) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPos({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy });
  };
  const onPointerUp = () => { dragRef.current = null; };

  if (!open) return null;

  return (
    <div
      data-testid="pdp-fullscreen-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={`${alt} — full screen image viewer`}
      className="fixed inset-0 z-[300] bg-black/95 flex flex-col animate-in fade-in duration-200"
      onWheel={onWheel}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 text-white/85 shrink-0">
        <span className="text-xs font-mono tabular-nums">{index + 1} / {images.length}</span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-testid="pdp-zoom-out-btn"
            onClick={zoomOut}
            disabled={scale <= MIN_SCALE}
            title="Zoom out"
            className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-30 disabled:pointer-events-none transition-colors duration-150"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="text-xs font-mono tabular-nums w-11 text-center">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            data-testid="pdp-zoom-in-btn"
            onClick={zoomIn}
            disabled={scale >= MAX_SCALE}
            title="Zoom in"
            className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-30 disabled:pointer-events-none transition-colors duration-150"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            type="button"
            data-testid="pdp-zoom-reset-btn"
            onClick={resetZoom}
            disabled={scale === 1}
            title="Reset zoom"
            className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-30 disabled:pointer-events-none transition-colors duration-150"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <button
            type="button"
            data-testid="pdp-zoom-close-btn"
            onClick={() => onOpenChange(false)}
            title="Close"
            className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 transition-colors duration-150"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Image stage */}
      <div
        className="relative flex-1 overflow-hidden flex items-center justify-center touch-none select-none"
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
        onDoubleClick={onDoubleClick}
      >
        <img
          src={resolveImg(images[index])}
          alt={`${alt} ${index + 1}`}
          draggable={false}
          className={`max-h-full max-w-full object-contain transition-transform duration-150 ${scale > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }}
        />
        {images.length > 1 && (
          <>
            <button
              type="button"
              data-testid="pdp-zoom-prev"
              onClick={goPrev}
              title="Previous image"
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 transition-colors duration-150"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              data-testid="pdp-zoom-next"
              onClick={goNext}
              title="Next image"
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 transition-colors duration-150"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="flex gap-2 justify-center px-4 py-3 overflow-x-auto shrink-0">
          {images.map((img, i) => (
            <button
              key={`${img}-${i}`}
              type="button"
              data-testid="pdp-zoom-thumb"
              onClick={() => { resetZoom(); onIndexChange(i); }}
              className={`h-14 w-14 rounded-md overflow-hidden border-2 shrink-0 transition-colors duration-150 ${i === index ? "border-white" : "border-white/20 hover:border-white/50"}`}
            >
              <img src={resolveImg(img)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
